import React, { useId, useState } from "react";

/**
 * A card with a clickable header that shows or hides its contents.
 *
 * Starts collapsed. When given a `storageKey`, whether it is open is
 * remembered per browser in localStorage. Storage can throw (private windows,
 * blocked site data), so every read and write is guarded and a failure simply
 * means "collapsed" -- the section still works, it just does not remember.
 *
 * Contents are rendered only while expanded, so a closed section costs
 * nothing and its fields are not in the tab order.
 */

const PREFIX = "collapsible:";

export function readOpen(storageKey) {
  if (!storageKey) return false;
  try {
    return window.localStorage.getItem(PREFIX + storageKey) === "1";
  } catch (e) {
    return false;
  }
}

export function writeOpen(storageKey, open) {
  if (!storageKey) return;
  try {
    if (open) window.localStorage.setItem(PREFIX + storageKey, "1");
    else window.localStorage.removeItem(PREFIX + storageKey);
  } catch (e) {
    // Not remembered; nothing else to do.
  }
}

export default function CollapsibleSection({
  title,
  storageKey,
  headerExtra = null,
  children,
  style,
}) {
  const [open, setOpen] = useState(() => readOpen(storageKey));
  const bodyId = useId();

  const toggle = () => {
    setOpen((was) => {
      const next = !was;
      writeOpen(storageKey, next);
      return next;
    });
  };

  return (
    <section style={{ marginBottom: 24, ...style }}>
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 24px" }}>
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            aria-controls={bodyId}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flex: 1,
              minWidth: 0,
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
              textAlign: "left",
              color: "#111827",
            }}
          >
            <span aria-hidden="true" style={{ width: 14, fontSize: 14, color: "#6b7280" }}>
              {open ? "▾" : "▸"}
            </span>
            <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>{title}</h2>
          </button>
          {headerExtra}
        </div>
        {open && (
          <div id={bodyId} style={{ padding: "0 24px 24px" }}>
            {children}
          </div>
        )}
      </div>
    </section>
  );
}
